import { z } from 'zod';
import { CASAS_DINHEIRO, CASAS_PRECO } from './aritmetica.ts';

/**
 * Zod é a única fonte de verdade de formato (.spec/002, seção 4).
 * O tipo TypeScript é inferido do schema, nunca escrito à mão em paralelo.
 */

export const uuidSchema = z.string().uuid();

/**
 * Dinheiro trafega como **string decimal** (`"1234.56"`), nunca como number.
 * Ponto flutuante em cálculo financeiro perde centavos de forma silenciosa.
 */
export const dinheiroSchema = z
  .string()
  .regex(/^-?\d{1,16}(\.\d{1,2})?$/, 'Valor monetário inválido (use ponto e até 2 casas).');

/** Quantidade — até 4 casas. */
export const quantidadeSchema = z
  .string()
  .regex(/^-?\d{1,14}(\.\d{1,4})?$/, 'Quantidade inválida (use ponto e até 4 casas).');

/**
 * Custo unitário — até **6 casas**. Custo médio e valor unitário de movimento.
 *
 * Separado de `dinheiroSchema` porque não é dinheiro: é a taxa que multiplica a
 * quantidade, e o dinheiro é o produto. Duas casas aqui viravam centavos lá —
 * um estoque de R$ 3.380,00 em 103 unidades tem média de R$ 32,815533…, e a R$
 * 32,82 a saída das 103 tirava R$ 3.380,46. Ver `CASAS_CUSTO`, em
 * `@construtora/domain`.
 *
 * **Só de resposta**, e continua sendo: custo médio é sempre calculado, nunca
 * digitado. O que mudou é a vizinhança — o **preço** de compra ganhou as mesmas
 * seis casas, e esse sim entra por payload. Ver `precoUnitarioSchema`.
 */
export const custoUnitarioSchema = z
  .string()
  .regex(/^-?\d{1,12}(\.\d{1,6})?$/, 'Custo unitário inválido (use ponto e até 6 casas).');

/**
 * Preço unitário de compra — até **6 casas**. Cotação, pedido, nota, último custo.
 *
 * **Isto reverte uma decisão que estava escrita aqui**, e a frase antiga era:
 * "nenhum payload pede seis casas — o que o usuário digita é preço de entrada,
 * que é dinheiro e continua com duas". A premissa era que preço de compra se
 * cobra em centavos. Combustível não: gasolina e óleo diesel são vendidos com
 * seis casas no litro, e a nota do posto traz `6.123456`. Com duas casas, o
 * preço era truncado antes de multiplicar — em 5.000 litros, `6.123456` cortado
 * em `6.12` erra R$ 17,28 numa nota só, e o erro é sempre para menos.
 *
 * **O produto continua sendo dinheiro de duas casas.** A escala é do preço, não
 * do total: `valor_total`, `valor_frete`, `valor_desconto` e os impostos seguem
 * em `dinheiroSchema`. O que a sexta casa faz é entrar na multiplicação em vez
 * de ser perdida antes dela — ver `multiplicarQuantidadePorValor`, em
 * `aritmetica.ts`.
 *
 * **Doze dígitos inteiros, e não dezesseis.** É o que sobra de `numeric(18,6)`,
 * e é o teto real da coluna: aceitar dezesseis aqui deixaria o schema passar um
 * valor que o Postgres recusa no `INSERT`, com erro de driver em vez de mensagem
 * de campo.
 *
 * Separado de `custoUnitarioSchema` embora as duas expressões sejam hoje
 * idênticas: uma é o que o fornecedor cobra e o usuário digita, a outra é o que
 * o estoque calcula e ninguém digita. A mensagem de erro fala de "preço" porque
 * é o que está escrito no rótulo do campo, e no dia em que uma das duas escalas
 * mudar, a outra não vai junto por acidente.
 */
export const precoUnitarioSchema = z
  .string()
  .regex(/^-?\d{1,12}(\.\d{1,6})?$/, 'Preço unitário inválido (use ponto e até 6 casas).');

/**
 * Quantas casas o preço unitário tem **naquele insumo**.
 *
 * A coluna aceita seis para todos, e isso não quer dizer que todos precisem de
 * seis: cimento é cotado em centavos, e um campo que oferecesse `32,500000`
 * pediria quatro zeros por linha a quem compra saco. O cadastro do insumo diz
 * quantas casas aquele material tem, e a tela usa esse número para desenhar o
 * campo de preço — duas no cimento, seis no diesel.
 *
 * **É só a escala de digitação e exibição.** A coluna do banco continua sendo
 * `numeric(18,6)` para todo mundo: gravar cada insumo numa precisão diferente
 * exigiria uma coluna por escala, e o valor deixaria de ser comparável entre
 * linhas da mesma nota.
 *
 * **Mínimo dois, e não zero.** Preço com zero casas existe — o milheiro de tijolo
 * a R$ 900 —, mas escrever `900` num campo de dinheiro que em toda outra tela
 * mostra `900,00` faria o mesmo número parecer de outra grandeza. Duas casas é o
 * piso do dinheiro, e é o que o resto do sistema exibe.
 *
 * **Máximo seis**, que é o da coluna: oferecer sete produziria um campo cujo
 * último dígito o `INSERT` descarta.
 */
export const CASAS_PRECO_MINIMAS = CASAS_DINHEIRO;
export const CASAS_PRECO_MAXIMAS = CASAS_PRECO;

/**
 * O padrão de quem não informa: **duas**.
 *
 * É o que o cadastro antigo tinha implicitamente, e é o que a maioria dos
 * insumos quer. O `default` do Zod e o `DEFAULT` da coluna dizem a mesma coisa,
 * de propósito: o payload que omite o campo e o `INSERT` que vem de fora da API
 * — importação de catálogo, script de carga — precisam concordar.
 */
export const CASAS_PRECO_PADRAO = CASAS_PRECO_MINIMAS;

export const casasDecimaisPrecoSchema = z
  .number()
  .int('As casas decimais são um número inteiro.')
  .min(CASAS_PRECO_MINIMAS, `O preço tem no mínimo ${CASAS_PRECO_MINIMAS} casas decimais.`)
  .max(CASAS_PRECO_MAXIMAS, `O preço tem no máximo ${CASAS_PRECO_MAXIMAS} casas decimais.`);

/** As opções que a tela oferece: 2, 3, 4, 5, 6. */
export const CASAS_PRECO_POSSIVEIS: readonly number[] = Array.from(
  { length: CASAS_PRECO_MAXIMAS - CASAS_PRECO_MINIMAS + 1 },
  (_, i) => CASAS_PRECO_MINIMAS + i,
);

/** Percentual com 4 casas — índice de rateio, tolerância. */
export const percentualSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Percentual inválido (use ponto e até 4 casas).');

/** Data de negócio, sem fuso: vencimento é o dia 10, não um instante. */
export const dataSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use o formato AAAA-MM-DD.');

/**
 * Hoje em `AAAA-MM-DD`, no fuso de quem chama.
 *
 * Data de negócio, e não instante: `toISOString().slice(0, 10)` daria o dia em
 * **UTC**, e o lançamento feito às 22h de São Paulo nasceria com a data de
 * amanhã.
 *
 * Vive no contrato porque a validação daqui depende dela — ver
 * `dataDoFatoSchema` —, e porque já existia copiada em quatro lugares: dois
 * formulários e dois services. Quatro cópias da mesma função são quatro chances
 * de a tela e o servidor discordarem sobre que dia é hoje.
 */
export function hojeIso(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');

  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/**
 * Data de um **fato**: nunca no futuro.
 *
 * É a data do que já aconteceu — o material entrou, a nota foi emitida, o
 * dinheiro saiu, a solicitação foi feita. Distinta das datas de **compromisso**
 * — vencimento, previsão de pagamento, prazo de entrega —, que são futuras por
 * definição e continuam com `dataSchema`.
 *
 * A varredura de 10/09/2026 achou o buraco inteiro: nenhum dos 24 campos de data
 * das 11 telas de lançamento tinha `min` ou `max`, e o servidor aceitava
 * 31/12/2099 e 01/01/1900 do mesmo jeito. Não é número torto em relatório: uma
 * entrada de estoque com data de 2099 **forma o custo médio do depósito hoje**,
 * e todo consumo lançado daqui até lá sai por ele. O cardex fica coerente
 * consigo mesmo, então nenhuma conferência acusa — o erro só aparece na margem
 * da obra, meses depois.
 *
 * ## Só o teto mora aqui
 *
 * O piso — "nada antes da data de bloqueio" — é parâmetro da conta, muda por
 * cliente e por mês, e o contrato não tem como conhecê-lo. Ele é conferido no
 * service. Este teto é o contrário: não depende de configuração nenhuma, porque
 * documento com data futura não é uma escolha de política, é uma contradição.
 *
 * ## O dia é o de quem valida, e a assimetria é segura
 *
 * A tela compara com o dia do navegador e o servidor com o dele. Se o servidor
 * roda em UTC e o usuário está no Brasil, o dia do servidor **nunca está atrás**
 * do dia do usuário — ele vira primeiro. A folga cai sempre para o lado
 * permissivo: no máximo o servidor aceita, por três horas, um "amanhã" que já é
 * hoje para ele. O contrário — a tela deixar digitar e o servidor recusar — não
 * acontece.
 */
export const dataDoFatoSchema = dataSchema.refine(
  (v) => v <= hojeIso(),
  'Esta data não pode ser futura: o documento registra o que já aconteceu. Para uma data a vencer, o campo é outro.',
);

/** Instante — sempre ISO 8601 em UTC. */
export const dataHoraSchema = z.string().datetime();

/**
 * Paginação e busca — a base de toda listagem.
 *
 * ## `.strict()`: parâmetro que não existe é erro, não é silêncio
 *
 * O padrão do Zod é **descartar** a chave desconhecida, e para query string esse
 * padrão é uma armadilha. `?termo=cimento` num cadastro de 336 insumos não
 * devolve erro: devolve `200` com os 336, que é indistinguível da resposta certa
 * a uma busca que não filtrou nada. `?pagina=2` devolve a página 1, e quem
 * integrar conclui que a lista acabou. `?xyz=lixo` devolve tudo.
 *
 * É a pior forma de uma API falhar. O erro não aparece em teste — a chamada
 * responde `200` —, aparece meses depois, e quem procura acha que o defeito é do
 * lado dele. Compare com `?pageSize=500`, que já devolvia `422` no primeiro
 * minuto: essa é a falha que se conserta antes de virar dado errado.
 *
 * **Não é só sobre integração de terceiros.** A tela não escreve filtro nenhum na
 * barra de endereços — as rotas são limpas e os filtros vivem no estado do React
 * —, então nenhum usuário digita parâmetro errado hoje. Mas o refator que renomeia
 * um filtro no contrato e esquece o `.api.ts` produz exatamente o mesmo silêncio,
 * e nesse caso o cliente enganado é a própria tela.
 *
 * ## Aqui, e não em cada listagem
 *
 * `.extend()` e `.omit()` herdam o modo estrito do objeto — `unknownKeys` no Zod
 * 3, `catchall` no 4, e `query-estrita.test.ts` confere. Um `.strict()` neste objeto alcança as **33** consultas que descendem
 * dele, incluindo as cinco que fazem `paginacaoSchema.omit({ q: true })`. Pôr o
 * `.strict()` em `listagemDeCadastroSchema` deixaria de fora as que estendem esta
 * base direto, que são a maioria.
 *
 * As consultas que **não** descendem daqui — os cinco painéis, o cardex, cidades,
 * NCM e as duas de insumo — levam `.strict()` próprio, e é o preço de não terem
 * paginação. Um `.strict()` esquecido numa consulta nova não quebra nada: ela
 * apenas volta a ignorar em silêncio, que é o defeito que se está fechando.
 *
 * ## O que muda para quem chamava errado
 *
 * As cinco listagens que removem o `q` — nota, pedido, solicitação, cotação e
 * transferência — passam a **recusar** `?q=`, que antes era ignorado. É quebra de
 * cliente existente, e é deliberada: a alternativa é continuar respondendo "achei
 * tudo" a quem pediu uma busca.
 */
export const paginacaoSchema = z
  .object({
    page: z.coerce.number().int().min(1).prefault(1),
    pageSize: z.coerce.number().int().min(1).max(200).prefault(50),
    sort: z.string().optional(),
    q: z.string().trim().min(1).max(200).optional(),
  })
  .strict();

export type Paginacao = z.infer<typeof paginacaoSchema>;

/**
 * Booleano opcional vindo da query string.
 *
 * Aceita o booleano e o texto `"true"`/`"false"`, que é como o valor chega de
 * fato pela URL — `?ativo=true` é a string `"true"`, e um `z.boolean()` puro
 * recusaria toda chamada feita pelo navegador.
 *
 * Ausente continua sendo **ausente**, e não `false`: a diferença entre "não
 * pedi este filtro" e "pedi o valor falso" é o que permite a um filtro trazer
 * tudo quando ninguém o mexeu.
 */
export const booleanoDeQuerySchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => v === true || v === 'true')
  .optional();

/**
 * Filtro de situação das listagens de cadastro.
 *
 * Ausente traz ativos e inativos; a tela manda `ativo=true` por padrão e o
 * usuário decide mostrar os inativos.
 */
export const filtroAtivoSchema = booleanoDeQuerySchema;

/** Paginação, busca e filtro de situação — o formato de toda listagem de cadastro. */
export const listagemDeCadastroSchema = paginacaoSchema.extend({ ativo: filtroAtivoSchema });

export function respostaListaSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
  });
}

/**
 * O que uma instalação de catálogo padrão devolve.
 *
 * Dois números, e os dois importam: `criados` é o que esta execução escreveu,
 * `total` é o tamanho do catálogo. A tela precisa da diferença para dizer "12
 * criados, 12 já existiam" em vez de "pronto" — repetir a instalação numa conta
 * já montada devolve `criados: 0`, e sem o `total` isso se lê como falha.
 *
 * Compartilhado porque os catálogos simples — unidades de medida, tipos de
 * movimentação, condições de pagamento e os dois históricos — respondem todos a
 * mesma coisa, e um cartão de instalação genérico na configuração da conta lê os
 * cinco pelo mesmo tipo. Plano de contas, CFOPs e insumos têm resposta própria:
 * eles atualizam, ignoram e criam vínculos, e um formato de dois números não
 * daria conta de contar o que fizeram.
 */
export const resultadoCatalogoPadraoSchema = z.object({
  criados: z.number().int(),
  total: z.number().int(),
});

export type ResultadoCatalogoPadrao = z.infer<typeof resultadoCatalogoPadraoSchema>;

/** Enums do domínio — códigos estáveis (.spec/001, seção 2). */
export const regimeTributarioSchema = z.enum(['NORMAL', 'SIMPLES', 'MEI']);

/**
 * Regime que pode ficar em aberto.
 *
 * "Não informado" **não é um quarto código**: é a ausência do regime, e no
 * banco isso já é `NULL` (`.spec/016`). Inventar um `'NI'` aqui obrigaria todo
 * cálculo de crédito a tratar dois jeitos de dizer a mesma coisa, e o primeiro
 * `IN ('NORMAL')` esquecido viraria imposto creditado por engano.
 *
 * A string vazia do `<select>` também entra, e sai como `null`: o formulário
 * não guarda `null` em campo de controle (ver os schemas de tela).
 *
 * Empresa **não** usa este schema — o regime dela decide o direito a crédito
 * (`.spec/001`, seção 2.2), e o `CHECK pessoas_empresa_exige_regime` recusa a
 * linha sem ele.
 */
export const regimeTributarioOpcionalSchema = z
  .union([regimeTributarioSchema, z.literal(''), z.null()])
  .transform((valor) => (valor === '' || valor === null ? null : valor))
  .prefault(null);

/**
 * Pessoa **física não tem regime tributário**: ele sai como "não informado".
 *
 * Regime de apuração é da pessoa jurídica — é o CNPJ que opta pelo Simples, é o
 * MEI que é uma empresa, e "lucro real / presumido" descreve como uma empresa
 * apura. Oferecer os três a um CPF era pedir uma informação que não existe, e o
 * que se gravava ali era palpite.
 *
 * Aplicado como **transformação, e não como recusa**: o campo desaparece da tela
 * quando o tipo é `PF` (ver `SecaoIdentificacao`), e recusar o payload por causa
 * de um valor que sobrou no formulário — o cadastro que era `PJ` com Simples e
 * passou a ser `PF` — devolveria um erro preso a um campo que não está mais na
 * tela. Zerar é o que a tela mostra e o que o banco guarda.
 *
 * Vale para cliente e fornecedor. **Empresa não passa por aqui**: ela é sempre
 * jurídica, e o regime dela é obrigatório porque decide o direito a crédito de
 * imposto (`.spec/001`, seção 2.2).
 */
export function semRegimeDePessoaFisica<
  T extends {
    tipo: z.infer<typeof tipoPessoaOuNaoInformadoSchema>;
    regimeTributario: z.infer<typeof regimeTributarioSchema> | null;
  },
>(dados: T): T {
  return dados.tipo === 'PF' ? { ...dados, regimeTributario: null } : dados;
}

/**
 * Tipo de pessoa **definido**: física ou jurídica.
 *
 * É o que empresa exige. Sem saber se é CPF ou CNPJ não há como conferir o
 * dígito verificador, e a empresa do grupo é justamente quem emite e recebe
 * documento fiscal.
 */
export const tipoPessoaSchema = z.enum(['PF', 'PJ']);

/**
 * Tipo como a coluna `pessoas.tipo` aceita, com `NI` — não informado.
 *
 * Existe para o cadastro que nasce incompleto: fornecedor levantado do
 * orçamento, ou órgão público cujo CNPJ não está à mão na hora do lançamento.
 * Toda pessoa **é** física ou jurídica; `NI` diz que ainda não se sabe qual,
 * e não um terceiro tipo de gente.
 *
 * Obrigar a escolher entre PF e PJ sem o documento produziria palpite gravado
 * como fato — e dado inventado não se distingue do verdadeiro depois.
 */
export const tipoPessoaOuNaoInformadoSchema = z.enum(['PF', 'PJ', 'NI']);
/**
 * Material ou serviço. Só `MATERIAL` movimenta estoque e admite NCM.
 * Os rótulos e as regras que cruzam o tipo com outros campos ficam em
 * `insumos.ts`, que é o dono do cadastro.
 */
export const tipoInsumoSchema = z.enum(['MATERIAL', 'SERVICO']);
/**
 * Natureza do local de estoque. É **etiqueta de classificação**, não regra:
 * nenhum cálculo olha para ela, e `OBRA` não aponta para o cadastro de obras
 * (decisão registrada em `.spec/015`). Os rótulos ficam em `depositos.ts`.
 */
export const tipoDepositoSchema = z.enum(['CENTRO_DISTRIBUICAO', 'OBRA', 'OUTROS']);
/**
 * Sentido financeiro do contrato: a construtora recebe ou paga por ele.
 * Os rótulos e as regras que cruzam o tipo com outros campos ficam em
 * `contratos.ts`, que é o dono do cadastro.
 */
export const tipoContratoSchema = z.enum(['RECEBIMENTO', 'PAGAMENTO']);
/**
 * Onde a condição de pagamento pode ser escolhida: no que a construtora paga,
 * no que ela recebe, ou nos dois.
 *
 * `AMBOS` é um terceiro código, e não a ausência de escolha: "30/60/90" serve
 * tanto à compra quanto à venda, e obrigar a cadastrar a mesma condição duas
 * vezes produziria duas linhas que o usuário teria de manter iguais à mão. Os
 * rótulos ficam em `condicoes-pagamento.ts`, que é o dono do cadastro.
 */
export const usoCondicaoPagamentoSchema = z.enum(['PAGAMENTO', 'RECEBIMENTO', 'AMBOS']);
/**
 * Sentido da movimentação de estoque: o material entra no depósito ou sai dele.
 *
 * Chama-se `sentido`, e não `tipo`, porque o cadastro inteiro já se chama "tipo
 * de movimentação de estoque" — `TipoMovimentacaoEstoque.tipo` obrigaria dois
 * nomes parecidos para coisas diferentes, e o tipo TypeScript do enum colidiria
 * com o do registro.
 *
 * São **dois códigos e nunca um terceiro**: entrada soma quantidade e valor na
 * linha Insumo × Depósito, saída subtrai pela quantidade × custo médio vigente
 * (`.spec/010`, Movimentação de estoque). Um "ajuste" ou "transferência" como
 * código próprio deixaria o cálculo sem saber para que lado andar — quem
 * distingue esses casos é o nome do cadastro, não o sentido.
 *
 * Os rótulos ficam em `tipos-movimentacao-estoque.ts`, que é o dono do cadastro.
 */
export const sentidoMovimentacaoEstoqueSchema = z.enum(['ENTRADA', 'SAIDA']);
export const situacaoDocumentoSchema = z.enum(['PENDENTE', 'ATENDIDO', 'CANCELADO', 'BAIXADO']);
export const situacaoNotaSchema = z.enum(['PENDENTE', 'CONFIRMADA']);
export const tipoMovimentoContaSchema = z.enum(['CREDITO', 'DEBITO']);
/** Sentido do dinheiro na conta corrente: crédito soma ao saldo, débito subtrai. */
export type TipoMovimentoConta = z.infer<typeof tipoMovimentoContaSchema>;
export const naturezaMovimentoSchema = z.enum(['PRINCIPAL', 'JUROS', 'OUTROS', 'DESCONTO']);
export const acaoSaldoTituloSchema = z.enum(['AUMENTA_SALDO', 'DIMINUI_SALDO', 'MANTEM_SALDO']);
export const politicaPedidoExcedenteSchema = z.enum(['BLOQUEAR', 'ALERTAR', 'TOLERANCIA']);
export const situacaoAprovacaoSchema = z.enum([
  'NAO_REQUER',
  'AGUARDANDO',
  'APROVADA',
  'REJEITADA',
]);

/* -------------------------------------------------------------------------- */
/* Ordem das linhas de um documento                                           */
/* -------------------------------------------------------------------------- */

/**
 * Para onde a linha vai quando alguém digita um número nela (`.spec/015`,
 * decisão 64).
 *
 * O número da linha **é** a posição: 1 é a primeira, `total` é a última. Então
 * mudar o número é mover a linha, e as que estavam no caminho andam uma casa —
 * a numeração continua sendo 1, 2, 3… sem buraco e sem repetição, porque ela
 * nunca é armazenada como um valor independente da ordem.
 *
 * É essa identidade que faz "não permitir número repetido" deixar de ser uma
 * validação e virar uma **impossibilidade**: não há estado em que duas linhas
 * tenham o mesmo número, nem por um instante, nem num payload malformado.
 *
 * ## O que a função devolve
 *
 * O **índice** de destino, base zero, já dentro dos limites. Base zero porque é
 * o que `useFieldArray.move` e `Array#splice` esperam; a conversão fica num
 * lugar só em vez de em cada tela.
 *
 * Fora da faixa, **encosta na ponta** em vez de recusar: quem digita 0 quer a
 * primeira, quem digita 99 numa lista de 7 quer a última, e uma recusa ali
 * obrigaria a pessoa a adivinhar o total para dizer o que já está claro. Número
 * não inteiro ou não numérico devolve a posição atual — não mover é a resposta
 * certa para uma instrução que não se entende.
 */
export function posicaoDoNumero(digitado: number, posicaoAtual: number, total: number): number {
  if (!Number.isFinite(digitado) || !Number.isInteger(digitado)) return posicaoAtual;
  if (total <= 0) return 0;

  const alvo = digitado - 1;

  if (alvo < 0) return 0;
  if (alvo > total - 1) return total - 1;

  return alvo;
}
